/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Platform } from '@angular/cdk/platform';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssTimePickerClockComponent } from './time-picker-clock/time-picker-clock.component';
import {
  OverlayScrollBehavior,
  OverlayScrollService
} from '../../services/overlay-scroll.service';
import {
  ShortTimeString,
  isValidShortTimeString,
  parseShortTimeString,
  buildShortTimeString
} from '@umss/shared/utils';

@Component({
  selector: 'umss-time-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    UmssIconComponent,
    UmssTimePickerClockComponent
  ],
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssTimePickerComponent),
      multi: true
    },
    provideNgxMask()
  ]
})
export class UmssTimePickerComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @HostBinding('style.display') display = 'contents';
  @ViewChild('nativeTimeInput')
  nativeTimeInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('minuteInput') minuteInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('hourInput') hourInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('clockComponent') clockComponentRef?: UmssTimePickerClockComponent;
  private elementRef = inject(ElementRef);
  private platform = inject(Platform);
  private overlayScrollService = inject(OverlayScrollService);

  protected readonly isMobile = this.platform.ANDROID || this.platform.IOS;

  private readonly _fallbackId = `umss-time-picker-${Math.random().toString(36).slice(2)}`;
  get inputId(): string {
    return this.id ?? this._fallbackId;
  }

  // Close when clicking outside the entire time-picker component
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isPopoverOpen) return;
    const clickedInside = this.elementRef.nativeElement.contains(
      event.target as Node
    );
    if (!clickedInside) {
      this.isPopoverOpen = false;
      this._onTouched();
      this.onBlur.emit();
      this._unregisterScrollBehavior();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isPopoverOpen) {
      this.calculatePosition();
    }
  }

  /** Internal FormControl drives the masked text input */
  inputControl = new FormControl<string>('', { nonNullable: true });
  private inputSubscription?: { unsubscribe: () => void };

  ngOnInit(): void {
    this.inputSubscription = this.inputControl.valueChanges.subscribe(
      (value) => {
        this.syncClockFromInput(value);
        const parsed = this.parseToShortTimeString(value);
        this._onChange(parsed);
        this.timeChange.emit(parsed);
      }
    );
  }

  ngOnDestroy(): void {
    this.inputSubscription?.unsubscribe();
    this._unregisterScrollBehavior();
  }

  /** Content */
  @Input() label?: string;
  @Input() placeholder = 'HH:mm';
  @Input() helperText?: string;
  @Input() errorMessage?: string;

  /** Variant */
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State */
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;

  /** HTML */
  @Input() id?: string;
  @Input() name?: string;
  @Input() scrollBehavior: OverlayScrollBehavior = 'close';

  /** Popup behaviour */
  @Input() use12Hours = true;
  @Input() requireOkToClose = false;
  /**
   * Preferred opening mode.
   * Priority when opening: this input (if set) > user's saved localStorage preference > 'clock'.
   * Leave undefined to let the user's last choice (or the fallback) decide.
   */
  @Input() defaultMode: 'clock' | 'keyboard' | undefined = undefined;

  /** Programmatic value binding (alternative to using formControl / ngModel) */
  @Input() set value(v: string | null | undefined) {
    if (v && isValidShortTimeString(v)) {
      this.writeValue(v);
    } else {
      this.writeValue(null);
    }
  }

  /** Outputs */
  @Output() timeChange = new EventEmitter<ShortTimeString | null>();
  /** Fires when the user explicitly commits a value (OK, auto-close on minute, Enter).
   *  Does NOT fire on cancel or outside-click dismissal. */
  @Output() committed = new EventEmitter<ShortTimeString | null>();
  /** Fires when the user switches between clock and keyboard mode inside the popup.
   *  Useful to react to mode changes; the component persists the choice automatically. */
  @Output() modeChange = new EventEmitter<'clock' | 'keyboard'>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFocus = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onBlur = new EventEmitter<void>();

  /** Popover + clock state */
  protected isPopoverOpen = false;
  protected dropdownStyle: Record<string, string> = {};
  protected clockHours = signal(0);
  protected clockMinutes = signal(0);
  protected clockView = signal<'hours' | 'minutes'>('hours');
  protected popoverMode = signal<'clock' | 'keyboard'>('clock');
  /** Stable display values for the keyboard inputs — set once on open/mode-switch
   *  so Angular's [value] binding never fights the user while they are typing. */
  protected keyboardHourDisplay = signal('');
  protected keyboardMinuteDisplay = signal('');
  private _overlayScrollId?: symbol;
  private _popoverValueAtOpen: ShortTimeString | null = null;

  /** AM/PM derived from clockHours: 0-11 → AM, 12-23 → PM */
  protected get currentAmPm(): 'AM' | 'PM' {
    return this.clockHours() < 12 ? 'AM' : 'PM';
  }

  /** CVA implementation */
  private _onChange: (value: ShortTimeString | null) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: ShortTimeString | null): void {
    const strValue = value ?? '';
    this.inputControl.setValue(strValue, { emitEvent: false });
    if (value && isValidShortTimeString(value)) {
      const { hours, minutes } = parseShortTimeString(value);
      this.clockHours.set(hours);
      this.clockMinutes.set(minutes);
    } else {
      this.clockHours.set(0);
      this.clockMinutes.set(0);
    }
  }

  registerOnChange(fn: (value: ShortTimeString | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
  }

  /** Template event handlers */
  onInputFocus(): void {
    if (this.disabled) return;
    if (!this.isMobile) {
      this.clockView.set('hours');
      this.openDropdown();
    }
    this.onFocus.emit();
  }

  onInputBlur(): void {
    if (this.disabled) return;
    // Don't close popover on blur — closing is handled by document:click
    this._onTouched();
    this.onBlur.emit();
  }

  onNativeTimeChange(event: Event): void {
    if (this.disabled) return;
    const input = event.target as HTMLInputElement;
    const value = input.value; // Format: HH:mm

    if (value && value.length === 5) {
      // value is in HH:mm format, which is a valid ShortTimeString
      this.inputControl.setValue(value, { emitEvent: false });
      this.syncClockFromInput(value);
      const parsed = this.parseToShortTimeString(value);
      this._onChange(parsed);
      this.timeChange.emit(parsed);
    } else if (!value) {
      // Clear the value
      this.inputControl.setValue('', { emitEvent: false });
      this.clockHours.set(0);
      this.clockMinutes.set(0);
      this._onChange(null);
      this.timeChange.emit(null);
    }
  }

  onClockIconClick(): void {
    if (this.disabled) return;
    if (this.isMobile) {
      // On mobile, the icon opens the native picker
      this.nativeTimeInputRef?.nativeElement.showPicker?.();
      this.nativeTimeInputRef?.nativeElement.focus();
      return;
    }
    if (this.isPopoverOpen) {
      this.isPopoverOpen = false;
      this._unregisterScrollBehavior();
    } else {
      this.clockView.set('hours');
      this.openDropdown();
    }
  }

  onPopoverClose(): void {
    this._onTouched();
  }

  onHourChange(h: number): void {
    this.clockHours.set(h);
    const formatted = buildShortTimeString(h, this.clockMinutes());
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
  }

  onMinuteChange(m: number): void {
    this.clockMinutes.set(m);
    const formatted = buildShortTimeString(this.clockHours(), m);
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
    if (!this.requireOkToClose) {
      this.committed.emit((formatted as ShortTimeString) ?? null);
      this.isPopoverOpen = false;
      this._unregisterScrollBehavior();
    }
  }

  onClockViewChange(v: 'hours' | 'minutes'): void {
    this.clockView.set(v);
  }

  /** Toggles popup between clock and keyboard mode */
  onToggleMode(): void {
    const newMode = this.popoverMode() === 'clock' ? 'keyboard' : 'clock';
    if (newMode === 'keyboard') {
      this._initKeyboardDisplayValues();
    }
    this.popoverMode.set(newMode);
    this.modeChange.emit(newMode);
    this._saveModePreference(newMode);
  }

  /** AM/PM toggle: shifts clockHours into the correct 12h range */
  onAmPmChange(period: 'AM' | 'PM'): void {
    const h = this.clockHours();
    let newH = h;
    if (period === 'PM' && h < 12) {
      newH = h + 12;
    } else if (period === 'AM' && h >= 12) {
      newH = h - 12;
    }
    if (newH === h) return;
    this.clockHours.set(newH);
    const formatted = buildShortTimeString(newH, this.clockMinutes());
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
  }

  /** Keyboard mode: hour input.
   *  We intentionally do NOT update keyboardHourDisplay here so Angular's
   *  [value] binding stays stable and never overwrites the user's typing.
   */
  onKeyboardHourInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (raw === '') return; // user cleared the field — wait for next digit
    const h = parseInt(raw, 10);
    if (isNaN(h)) return;
    const clamped = Math.max(0, Math.min(23, h));
    this.clockHours.set(clamped);
    const formatted = buildShortTimeString(clamped, this.clockMinutes());
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
    // Auto-jump to minute input once the user has typed 2 digits
    if (raw.length === 2) {
      this.minuteInputRef?.nativeElement.focus();
      this.minuteInputRef?.nativeElement.select();
    }
  }

  /** Keyboard mode: minute input.
   *  Same pattern as onKeyboardHourInput.
   */
  onKeyboardMinuteInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (raw === '') return;
    const m = parseInt(raw, 10);
    if (isNaN(m)) return;
    const clamped = Math.max(0, Math.min(59, m));
    this.clockMinutes.set(clamped);
    const formatted = buildShortTimeString(this.clockHours(), clamped);
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
  }

  /** OK button: commits draft values and closes popup */
  onOkClick(): void {
    const formatted = buildShortTimeString(
      this.clockHours(),
      this.clockMinutes()
    );
    if (formatted) {
      this.inputControl.setValue(formatted, { emitEvent: true });
      this.committed.emit(formatted as ShortTimeString);
    } else {
      this._onChange(null);
      this.timeChange.emit(null);
      this.committed.emit(null);
    }
    this.isPopoverOpen = false;
    this._unregisterScrollBehavior();
  }

  /** CANCEL button: reverts to value stored at popup-open time */
  onCancelClick(): void {
    this.writeValue(this._popoverValueAtOpen);
    this._onChange(this._popoverValueAtOpen);
    this.timeChange.emit(this._popoverValueAtOpen);
    this.isPopoverOpen = false;
    this._unregisterScrollBehavior();
  }

  private openDropdown(): void {
    this._popoverValueAtOpen = isValidShortTimeString(this.inputControl.value)
      ? (this.inputControl.value as ShortTimeString)
      : null;
    this.popoverMode.set(this._getInitialMode());
    // Always prime keyboard display values so they're ready whether the user
    // starts in keyboard mode or switches to it during this popup session.
    this._initKeyboardDisplayValues();
    this.calculatePosition();
    this.isPopoverOpen = true;
    this._registerScrollBehavior();
    // Move focus to the first interactive element inside the popup
    // after Angular renders it (setTimeout defers past the current CD cycle).
    this._focusPopupInput();
  }

  /** Programmatically opens the popup (e.g. triggered by a sibling field committing).
   *  No-op on mobile or when already open.
   */
  openPopup(): void {
    if (this.disabled || this.isMobile || this.isPopoverOpen) return;
    this.clockView.set('hours');
    this.openDropdown();
  }

  /** Moves browser focus into the popup's first interactive element.
   *  - keyboard mode → hour text input
   *  - clock mode    → clock SVG dial (tabindex="0")
   */
  private _focusPopupInput(): void {
    setTimeout(() => {
      if (this.popoverMode() === 'keyboard') {
        this.hourInputRef?.nativeElement.focus();
      } else {
        this.clockComponentRef?.focusDial();
      }
    }, 0);
  }

  /** Snapshots current clock state into the keyboard display signals.
   *  Shows empty strings when there is no committed value yet, so the
   *  keyboard inputs show the placeholder ("--") rather than "00".
   *  Called on popup open and whenever the user switches to keyboard mode.
   */
  private _initKeyboardDisplayValues(): void {
    if (this.hasClockValue) {
      this.keyboardHourDisplay.set(
        this.clockHours().toString().padStart(2, '0')
      );
      this.keyboardMinuteDisplay.set(
        this.clockMinutes().toString().padStart(2, '0')
      );
    } else {
      this.keyboardHourDisplay.set('');
      this.keyboardMinuteDisplay.set('');
    }
  }

  // ─── Mode preference (localStorage) ───────────────────────────────────────

  private static readonly _MODE_PREF_KEY = 'umss:time-picker:mode';

  /**
   * Resolves the mode to use when opening the popup:
   * 1. Explicit `defaultMode` input (if set)
   * 2. User's last saved preference in localStorage
   * 3. 'clock' fallback
   */
  private _getInitialMode(): 'clock' | 'keyboard' {
    if (this.defaultMode !== undefined) return this.defaultMode;
    return this._loadModePreference() ?? 'clock';
  }

  private _saveModePreference(mode: 'clock' | 'keyboard'): void {
    try {
      localStorage.setItem(UmssTimePickerComponent._MODE_PREF_KEY, mode);
    } catch {
      // localStorage unavailable (private browsing, quota exceeded, etc.)
    }
  }

  private _loadModePreference(): 'clock' | 'keyboard' | null {
    try {
      const saved = localStorage.getItem(
        UmssTimePickerComponent._MODE_PREF_KEY
      );
      if (saved === 'clock' || saved === 'keyboard') return saved;
    } catch {
      // localStorage unavailable
    }
    return null;
  }

  private _registerScrollBehavior(): void {
    this._unregisterScrollBehavior();
    this._overlayScrollId = this.overlayScrollService.register({
      behavior: this.scrollBehavior,
      onFollow: () => this.calculatePosition(),
      onClose: () => {
        this.isPopoverOpen = false;
        this._onTouched();
        this.onBlur.emit();
        this._unregisterScrollBehavior();
      }
    });
  }

  private _unregisterScrollBehavior(): void {
    if (this._overlayScrollId !== undefined) {
      this.overlayScrollService.unregister(this._overlayScrollId);
      this._overlayScrollId = undefined;
    }
  }

  private calculatePosition(): void {
    const host = this.elementRef.nativeElement;
    const trigger = host.querySelector('.input-wrapper');
    if (!trigger) return;

    const rect = (trigger as HTMLElement).getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const popoverHeight = 360;
    const popoverWidth = 288;

    const style: Record<string, string> = {
      'min-width': `${rect.width}px`
    };

    // Vertical: prefer below, flip above if not enough space
    if (
      viewportHeight - rect.bottom < popoverHeight &&
      rect.top > viewportHeight - rect.bottom
    ) {
      style['bottom'] = `${viewportHeight - rect.top + 4}px`;
    } else {
      style['top'] = `${rect.bottom + 4}px`;
    }

    // Horizontal: prefer left-aligned, flip if not enough space
    if (viewportWidth - rect.left < popoverWidth && rect.right > popoverWidth) {
      style['right'] = `${viewportWidth - rect.right}px`;
    } else {
      style['left'] = `${rect.left}px`;
    }

    this.dropdownStyle = style;
  }

  /** True when inputControl has a valid time value.
   *  Used to control the display of the hand in the clock. */
  protected get hasClockValue(): boolean {
    return isValidShortTimeString(this.inputControl.value);
  }

  get iconSize(): string {
    switch (this.size) {
      case 'Mini':
        return '14px';
      case 'Small':
        return '16px';
      case 'Large':
        return '20px';
      default:
        return '18px';
    }
  }

  /** Parses the masked text input to a valid ShortTimeString or null */
  private parseToShortTimeString(value: string): ShortTimeString | null {
    if (!value || value.length < 5) return null;
    return isValidShortTimeString(value) ? (value as ShortTimeString) : null;
  }

  /** Syncs clock state from text input (only when fully valid) */
  private syncClockFromInput(value: string): void {
    if (!isValidShortTimeString(value)) return;
    const { hours, minutes } = parseShortTimeString(value as ShortTimeString);
    this.clockHours.set(hours);
    this.clockMinutes.set(minutes);
  }
}
